import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationButton, ModalContainer } from "../../components";
import { returnIsValid } from "../../Main/useConstants";
import { CatalogueMultiSelect } from "../../../../../MainComponents/Catalogues/CatalogueMultiSelect";
import { useReportContext } from "../../../../ReportContext/useReportContext";
import { useActionsCatalogue } from "../../../../../../hooks/useActionsCatalogue";
import {
  ACTION_TYPES,
  normalizeProposedAction,
} from "../../../../../../helpers/correctiveActions";
import { caseTypeLabel } from "../../../../../../helpers/caseClassification";

const initialMeasure = {
  type: ACTION_TYPES.MEASURE,
  actions: [],
  another: [],
  involved: null,
  case_type: null,
  details: "",
};

// Las sanciones se aplican a quien resulto señalado en la investigacion.
const SANCTIONABLE_KINDS = ["offender", "accomplice"];

const validateForm = (form) => {
  const errors = {};

  if (form.actions.length === 0 && form.another.filter(Boolean).length === 0) {
    errors.actions = true;
  }
  if (form.another.some((text) => text.trim() === "")) {
    errors.actions = true;
  }
  // Una sancion sin destinatario no se puede arrastrar despues a Resolucion,
  // que registra las sanciones por persona.
  if (form.type === ACTION_TYPES.SANCTION && !form.involved) {
    errors.involved = true;
  }

  return [Object.keys(errors).length === 0, errors];
};

export const AddMeasure = ({
  show,
  onHide,
  measures,
  setMeasures,
  selectedConclusionMeasure,
  setSelectedConclusionMeasure,
  involved,
  caseTypes = [],
}) => {
  const [t] = useTranslation("investigation");
  const [tReport] = useTranslation("report");
  const theme = useTheme();
  // Mismas listas fijas que usa Resolucion cuando el tenant no tiene catalogo,
  // para que el investigador y quien resuelve vean exactamente lo mismo.
  const { measureOptions, sanctionsOptions } = useReportContext();

  const [measure, setMeasure] = useState(initialMeasure);
  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  const {
    catalogue,
    isOwn,
    rootPath,
    isLoading: isCatalogueLoading,
  } = useActionsCatalogue(measure.type);

  const actionTypeOptions = useMemo(
    () => [
      { value: ACTION_TYPES.MEASURE, label: t("Conclusion.action_type_measure") },
      {
        value: ACTION_TYPES.SANCTION,
        label: t("Conclusion.action_type_sanction"),
      },
    ],
    [t]
  );

  // Clasificaciones capturadas en la carpeta (folder.case_classification). Un
  // caso puede tener varias, y de ahi la pregunta: por cual va esta propuesta.
  const caseTypeOptions = useMemo(
    () =>
      caseTypes.map((caseType) => ({
        value: caseType.path,
        label: caseTypeLabel(caseType),
      })),
    [caseTypes]
  );

  // Con una sola clasificacion no hay nada que elegir: se asume esa.
  useEffect(() => {
    if (caseTypes.length !== 1) return;
    setMeasure((prev) =>
      prev.case_type ? prev : { ...prev, case_type: caseTypes[0] }
    );
  }, [caseTypes]);

  const handleCaseTypeChange = (path) =>
    setMeasure((prev) => ({
      ...prev,
      case_type: caseTypes.find((item) => item.path === path) || null,
    }));

  // Señalados y complices de la investigacion, que son los sancionables.
  const sanctionableOptions = useMemo(() => {
    if (!involved) return [];
    return SANCTIONABLE_KINDS.flatMap((kind) =>
      (involved[kind] || []).map((person) => ({
        value: `${person.name}-${person.rol || kind}`,
        label: person.name,
        data: person,
      }))
    );
  }, [involved]);

  // Para editar una accion existente (normaliza registros previos).
  useEffect(() => {
    if (selectedConclusionMeasure !== null) {
      setMeasure({
        ...initialMeasure,
        ...normalizeProposedAction(measures[selectedConclusionMeasure]),
      });
    }
  }, [selectedConclusionMeasure]);

  // Resetear los valores cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        resetValues();
      }, 200);
    }
  }, [show]);

  // Validar en cada cambio
  useEffect(() => {
    if (triedToSave) {
      const [, currentErrors] = validateForm(measure);
      setErrors(currentErrors);
    }
  }, [measure]);

  const handleTypeChange = (type) => {
    // El catalogo cambia con el tipo, asi que lo elegido deja de aplicar.
    setMeasure((prev) => ({
      ...prev,
      type,
      actions: [],
      involved: type === ACTION_TYPES.SANCTION ? prev.involved : null,
    }));
  };

  const handleInvolvedChange = (value) => {
    const selected = sanctionableOptions.find(
      (option) => option.value === value
    );
    setMeasure((prev) => ({ ...prev, involved: selected?.data || null }));
  };

  const handleSave = () => {
    setTriedToSave(true);
    const [isValid, currentErrors] = validateForm(measure);
    setErrors(currentErrors);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    const measureToSave = {
      ...measure,
      another: measure.another.filter(Boolean),
    };

    if (selectedConclusionMeasure !== null) {
      const newMeasures = _.cloneDeep(measures);
      newMeasures[selectedConclusionMeasure] = measureToSave;
      setMeasures(newMeasures);
    } else {
      setMeasures([...measures, measureToSave]);
    }
    onHide();
  };

  const resetValues = () => {
    setMeasure(_.cloneDeep(initialMeasure));
    setErrors({});
    setTriedToSave(false);
    setSelectedConclusionMeasure(null);
  };

  const isSanction = measure.type === ACTION_TYPES.SANCTION;
  // "another" no va en la lista: el texto libre tiene su propio campo.
  const fallbackOptions = (
    isSanction ? sanctionsOptions : measureOptions
  ).filter((option) => option.value !== "another");
  const involvedValue = measure.involved
    ? `${measure.involved.name}-${measure.involved.rol}`
    : "";

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Stack spacing={2}>
        {/* Header */}
        <Box>
          <Typography
            fontWeight={700}
            fontSize={28}
            color={theme.palette.ethicsBlue.main}
          >
            {t("Conclusion.measures_corrective_modal_title")}
          </Typography>
          <Typography fontSize={16} color="oxfordBlue">
            {t("Conclusion.measures_corrective_modal_description")}
          </Typography>

          <hr style={{ marginBottom: 0 }} />
        </Box>

        {/* Tipo: define de que rama del catalogo se elige y si lleva
            destinatario. Es lo que permite que la propuesta caiga despues en
            Medidas o en Medida disciplinaria dentro de Resolucion. */}
        <EGBasicSelect
          label={`${t("Conclusion.action_type")}:`}
          options={actionTypeOptions}
          value={measure.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        />

        {isSanction &&
          (sanctionableOptions.length > 0 ? (
            <EGBasicSelect
              label={`${t("Conclusion.involved_to_sanction")}:`}
              options={sanctionableOptions}
              value={involvedValue}
              onChange={(e) => handleInvolvedChange(e.target.value)}
              isValid={returnIsValid(errors?.involved, triedToSave) ?? null}
            />
          ) : (
            <Typography fontSize={14} color={theme.palette.red.main}>
              {t("Conclusion.no_sanctionable_involved")}
            </Typography>
          ))}

        {!isCatalogueLoading && (
          <CatalogueMultiSelect
            label={
              isSanction
                ? t("Conclusion.action_type_sanction")
                : t("Conclusion.suggestion")
            }
            placeholder={t("Conclusion.select_one_or_more")}
            catalogue={catalogue}
            isOwn={isOwn}
            rootPath={rootPath}
            options={fallbackOptions}
            value={measure.actions}
            onChange={(actions) =>
              setMeasure((prev) => ({ ...prev, actions }))
            }
            another={measure.another}
            onAnotherChange={(another) =>
              setMeasure((prev) => ({ ...prev, another }))
            }
            isValid={returnIsValid(errors?.actions, triedToSave) ?? undefined}
            freeTextLabel={tReport("Resolution.another")}
            addFreeTextTitle={t("Conclusion.add_free_text")}
          />
        )}

        {/* Por cual clasificacion del caso va esta propuesta. Solo aparece si
            hay mas de una: con una sola ya quedo elegida sola. */}
        {caseTypeOptions.length > 1 && (
          <EGBasicSelect
            label={`${t("Conclusion.action_case_type")}:`}
            options={caseTypeOptions}
            value={measure.case_type?.path ?? ""}
            onChange={(e) => handleCaseTypeChange(e.target.value)}
            placeholder={t("Conclusion.action_case_type_placeholder")}
            isValid={null}
            showHelpText={false}
          />
        )}

        <EGInput
          label={t("Conclusion.details")}
          value={measure.details}
          onChange={(e) =>
            setMeasure((prev) => ({ ...prev, details: e.target.value }))
          }
          multiline
          rows={3}
        />

        {/* Botones */}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <InvestigationButton
            label={t("Conclusion.cancel")}
            variant="main"
            outlined
            onClick={onHide}
          />
          <InvestigationButton
            label={t("Conclusion.save")}
            variant="main"
            onClick={handleSave}
          />
        </Stack>
      </Stack>
    </ModalContainer>
  );
};
